// Crash-reporter boundary for the WebView layer.
//
// Mirrors the Rust `songbird_analytics::crash::CrashReporter` trait: the
// vendor (Sentry today) sits behind this interface so swapping backends is
// a new impl plus the one line in `sentry.ts` that picks it. Consent
// gating + path scrubbing are the impl's responsibility (using the shared
// `scrubUrl` below) — the same privacy contract as the backend.

export interface CrashConfig {
  /** Vendor ingest endpoint (Sentry DSN). Empty ⇒ the impl must no-op. */
  dsn: string;
  /** Release/version tag; groups front- and back-end crashes per build. */
  release?: string;
  /** Deployment environment tag. */
  environment: 'development' | 'production';
  /** Performance-trace sampling rate, 0..1. */
  tracesSampleRate: number;
}

/**
 * One crash-reporting backend for the WebView. Implementations MUST gate
 * outbound events on consent (via `setConsent`) and scrub local paths
 * (via `scrubUrl`) before anything leaves the device.
 */
export interface CrashReporter {
  /**
   * Initialize the SDK. Idempotent — inits at most once even if called
   * repeatedly, and no-ops when `cfg.dsn` is empty. Deferred until first
   * consent so DSN-less / no-consent builds never load the SDK.
   */
  init(cfg: CrashConfig): void;
  /** Update the in-memory consent mirror that gates outbound events. */
  setConsent(granted: boolean): void;
}

/**
 * Redact local filesystem paths out of a URL-shaped string. Songbird
 * project paths and asset URLs can embed the user's home directory
 * (macOS `/Users/`, Linux `/home/`, Windows `C:\Users\`, or a `file:`
 * URL). Matching strings collapse to `[redacted-local-path]`; anything
 * else passes through unchanged. Mirrors the Rust-side
 * `songbird_analytics::scrub::redact_local_paths`.
 */
export function scrubUrl(url: string): string {
  if (
    url.startsWith('file:') ||
    url.includes('/Users/') ||
    url.includes('/home/') ||
    /[A-Za-z]:\\Users\\/i.test(url)
  ) {
    return '[redacted-local-path]';
  }
  return url;
}
